import { FC, useState, useMemo, useContext } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { Button, App } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { DictContext } from "@/layout/context/DictContext";
import { getFileUrl } from "@/apis/file";
import zioncom from 'images/system/zioncom.png';
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { InventoryLotItem } from "@/types/inventoryV2";
import truncateString from "@/utils/stringOver";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },

  // 标题部分
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },
  title: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center' as any
  },
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black",
    marginTop: 8
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格样式
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as any,
    letterSpacing: 0,
    color: '#000'
  },

  // 条码单元格
  barcodeCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 1,
    fontSize: 5,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#000'
  },

  // 表头单元格
  tableHeaderCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center' as any
  },
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center' as any,
    fontSize: 6,
    color: '#000',
    letterSpacing: -1
  }
});

// 扩展类型以包含条码URL
interface CodeUrlItem extends InventoryLotItem {
  codeUrl?: string;
}

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

interface SpecialOutPrintProps {
  reqNumber: string;
  datas: Array<InventoryLotItem>;
  onStart?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
}

const SpecialOutPrint: FC<SpecialOutPrintProps> = ({ reqNumber, datas, onStart, onEnd, disabled = false }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);
  const { printSetting } = useContext(DictContext);

  // 获取公司logo
  const { picUrl, name, address } = useMemo(() => {
    let [picUrl, name, address] = ['', '', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLocation" && item.value) address = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name, address };

  }, [printSetting])

  // 生成条码
  const handleCode = (code: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!code || code === '-') {
        resolve('');
        return;
      }
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, code, {
        format: "CODE128",
        width: 10,
        height: 20,
        displayValue: false,
        margin: 0,
        marginBottom: 6,
        marginTop: 4
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject("条码生成失败");
        }
      }, 'image/png');
    });
  };

  // 生成PDF文档
  const MyDocument = (codeItems: CodeUrlItem[], reqBarcode: string) => {
    const itemsPerPage = 14; // 每页显示40行
    const datas2D = convertTo2DArray<CodeUrlItem>(codeItems, itemsPerPage);

    return (
      <Document>
        {
          datas2D.map((items, pageIndex) => (
            <Page size="A4" orientation="portrait" style={styles.page} key={pageIndex}>
              {/* 标题栏 - 只在第一页显示 */}
              {pageIndex === 0 && (
                <View style={styles.titleSection}>
                  <Text style={styles.title}>{name}---{t('warehouse.specialOutbound')}</Text>
                  {reqBarcode && (
                    <View style={styles.titleBarcode}>
                      <Image src={reqBarcode} style={{ width: 100, height: 28 }} />
                      <Text style={styles.barcodeText}>{reqNumber || ''}</Text>
                    </View>
                  )}
                </View>
              )}

              {/* 表格 */}
              <View style={styles.table}>
                {/* 头部信息 */}
                <View style={styles.tableRow} fixed>
                  <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.outNo'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '23%' }]}> </View>

                  <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.innerNo'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '33%' }]}> </View>

                  <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.outData'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '10%' }]}> </View>
                </View>
                {/* 头部信息 */}
                <View style={styles.tableRow} fixed>
                  <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.outWare'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '23%' }]}> </View>

                  <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.llorg'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '33%' }]}> </View>

                  <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.llper'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '10%' }]}> </View>
                </View>
                {/* 表头 */}
                <View style={styles.tableRow} fixed>
                  <View style={[styles.tableHeaderCol, { width: '5%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '30%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '45%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('product.brand1'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('order.quantity'))}</Text>
                  </View>
                  <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('product.uom'))}</Text>
                  </View>
                </View>

                {/* 表体 */}
                {items.map((item: CodeUrlItem, index: number) => {
                  const rowNumber = pageIndex * itemsPerPage + index + 1;
                  return (
                    <View style={styles.tableRow} key={index}>
                      <View style={[styles.tableCol, { width: '5%' }]}>
                        <Text style={styles.fontStyle}>{rowNumber}</Text>
                      </View>
                      <View style={[styles.barcodeCol, { width: '30%' }]}>
                        {item.codeUrl && (
                          <Image src={item.codeUrl} style={{ width: "90%", height: 28 }} />
                        )}
                        <Text style={[styles.fontStyle, { fontSize: item.codeUrl ? 5 : 6, marginTop: item.codeUrl ? -5 : 0 }]}>
                          {getBlankWord(`${item?.product?.code ?? ''} / ${item?.product?.name ?? ''}`)}
                        </Text>
                      </View>
                      <View style={[styles.tableCol, { width: '45%' }]}>
                        <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                          {getBlankWord(truncateString(item.product?.model || '-', 38), true)}
                        </Text>
                      </View>
                      <View style={[styles.tableCol, { width: '10%' }]}>
                        <Text style={styles.fontStyle}>{getBlankWord(String(item.count || '0'))}</Text>
                      </View>
                      <View style={[styles.tableCol, { width: '10%' }]}>
                        <Text style={[styles.fontStyle, { flexWrap: 'wrap' }]}>{getBlankWord(item.product?.uom || '-')}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>

              {/* Logo图片 - 只在第一页显示 */}
              {pageIndex === 0 && (
                <Image fixed src={picUrl || zioncom} style={{
                  position: "absolute",
                  top: 24,
                  left: 30,
                  flexGrow: 1,
                  height: 30,
                  objectFit: "scale-down"
                }} />
              )}

              <View style={styles.bottomInfo}>
                <View style={styles.bottomItem}>
                  <Text style={styles.bottomText}>{t('warehouse.outper')}</Text>
                </View>
                <View style={styles.bottomItem}>
                  <Text style={styles.bottomText}>{t('warehouse.wareper')}</Text>
                </View>
                <View style={styles.bottomItem}>
                  <Text style={styles.bottomText}>{t('warehouse.outCheckDate')}</Text>
                </View>
              </View>

              {/* 页脚 */}
              <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
                `${t('purchase.pageIndex', { index: pageNumber, total: totalPages })}`
              } fixed />
            </Page>
          ))
        }
      </Document>
    );
  };

  // 处理PDF打印
  const handlePDF = async () => {
    if (!datas || datas.length === 0) {
      return notification.error({
        placement: 'top',
        message: t('purchase.printErr'),
        description: t('bom.errTip')
      });
    }

    setLoading(true);
    if (onStart) onStart();

    try {
      // 生成需求单号条码
      let reqBarcode = '';
      try {
        if (reqNumber) {
          reqBarcode = await handleCode(reqNumber);
        }
      } catch (error) {
        console.error('需求单号条码生成失败:', error);
      }

      // 为每个产品生成条码
      let codeItems: CodeUrlItem[] = [];
      for (let i = 0; i < datas.length; i++) {
        const item = datas[i];
        let codeUrl = '';
        try {
          if (item.product?.code) {
            codeUrl = await handleCode(item.product.code);
          }
        } catch (error) {
          console.error('条码生成失败:', item.product?.code, error);
        }
        codeItems.push({
          ...item,
          codeUrl
        });
      }

      // 生成PDF文档
      const pageElement = MyDocument(codeItems, reqBarcode);
      if (!pageElement) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        return;
      }

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: '打印失败' });
        } finally {
          setLoading(false);
          if (onEnd) onEnd();
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: '生成PDF失败' });
      setLoading(false);
      if (onEnd) onEnd();
    }
  };

  return (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePDF}
      loading={loading}
      disabled={disabled}
    >
      {t('common.printInfo')}
    </Button>
  );

};
export default SpecialOutPrint;